/**
 * Connection Test Button Component
 * 接続テストボタンコンポーネント
 * 
 * World-class UI/UX for testing external platform connections
 * Features:
 * - Beautiful loading animations with progress feedback
 * - Success/failure animations with haptic-style feedback
 * - Comprehensive error handling and user guidance
 * - Accessible design (WCAG AAA) with proper ARIA labels
 * - Professional status reporting and troubleshooting hints
 */

import React, { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Collapse,
  Stack,
  Chip,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import {
  PlayArrow as TestIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Refresh as RetryIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import type { ExternalPlatform } from '../../../types/external-sync';

export interface ConnectionTestResult {
  success: boolean;
  platform: ExternalPlatform;
  responseTime?: number;
  message: string;
  details?: string;
  suggestions?: string[];
  timestamp: Date;
}

export interface ConnectionTestButtonProps {
  platform: ExternalPlatform;
  loginId?: string;
  password?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'outlined' | 'text';
  onTest?: (credentials: { loginId: string; password: string }) => Promise<ConnectionTestResult>;
  onTestComplete?: (result: ConnectionTestResult) => void;
  className?: string;
}

type TestState = 'idle' | 'testing' | 'success' | 'error' | 'warning';

const TEST_STEPS = [
  { label: '認証情報を検証中...', duration: 1000 },
  { label: 'プラットフォームに接続中...', duration: 2000 },
  { label: 'ログイン状態を確認中...', duration: 1500 },
  { label: 'データアクセス権限を確認中...', duration: 1000 },
] as const;

const PLATFORM_NAMES = {
  hotpepper: 'ホットペッパービューティー',
  rakuten: '楽天ビューティー',
  minimo: 'minimo',
  epark: 'EPARKビューティー',
  kanzashi: 'Kanzashi',
} as const;

const ConnectionTestButton: React.FC<ConnectionTestButtonProps> = memo(({
  platform,
  loginId = '',
  password = '',
  disabled = false,
  size = 'medium',
  variant = 'contained',
  onTest,
  onTestComplete,
  className,
}) => {
  const theme = useTheme();
  const [testState, setTestState] = useState<TestState>('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const platformName = PLATFORM_NAMES[platform];

  const isCredentialsProvided = loginId.trim() !== '' && password.trim() !== '';
  const isTestDisabled = disabled || !isCredentialsProvided || testState === 'testing';

  const handleTest = useCallback(async () => {
    if (!onTest || isTestDisabled) return;

    setTestState('testing');
    setCurrentStep(0);
    setTestResult(null);
    setShowDetails(false);

    try {
      // Simulate test progress through steps
      for (let i = 0; i < TEST_STEPS.length; i++) {
        setCurrentStep(i);
        await new Promise(resolve => setTimeout(resolve, TEST_STEPS[i].duration));
      }

      // Execute actual test
      const result = await onTest({
        loginId: loginId.trim(),
        password: password.trim(),
      });

      setTestResult(result);
      setTestState(result.success ? 'success' : 'error');
      onTestComplete?.(result);

      // Auto-hide success message after 3 seconds
      if (result.success) {
        setTimeout(() => {
          if (testState === 'success') {
            setTestState('idle');
            setTestResult(null);
          }
        }, 3000);
      }
    } catch (error) {
      const errorResult: ConnectionTestResult = {
        success: false,
        platform,
        message: '接続テストでエラーが発生しました',
        details: error instanceof Error ? error.message : '不明なエラー',
        suggestions: [
          'ネットワーク接続を確認してください',
          '認証情報が正しいかご確認ください',
          'しばらく時間をおいてから再試行してください',
        ],
        timestamp: new Date(),
      };
      
      setTestResult(errorResult);
      setTestState('error');
      onTestComplete?.(errorResult);
    }
  }, [onTest, isTestDisabled, loginId, password, platform, onTestComplete, testState]);

  const handleRetry = useCallback(() => {
    setTestState('idle');
    setTestResult(null);
    setShowDetails(false);
    handleTest();
  }, [handleTest]);

  const getButtonProps = () => {
    const baseProps = {
      size,
      variant,
      disabled: isTestDisabled,
      onClick: testState === 'error' ? handleRetry : handleTest,
      'aria-label': `${platformName}への接続をテスト`,
      'aria-describedby': testResult ? 'test-result' : undefined,
    };

    switch (testState) {
      case 'testing':
        return {
          ...baseProps,
          color: 'primary' as const,
          startIcon: <CircularProgress size={16} />,
          children: 'テスト中...',
        };
      case 'success':
        return {
          ...baseProps,
          color: 'success' as const,
          startIcon: <SuccessIcon />,
          children: '接続成功',
        };
      case 'error':
        return {
          ...baseProps,
          color: 'error' as const,
          startIcon: <RetryIcon />,
          children: '再試行',
        };
      case 'warning':
        return {
          ...baseProps,
          color: 'warning' as const,
          startIcon: <WarningIcon />,
          children: '警告あり',
        };
      default:
        return {
          ...baseProps,
          color: 'primary' as const,
          startIcon: <TestIcon />,
          children: '接続テスト',
          disabled: isTestDisabled,
        };
    }
  };

  const buttonProps = getButtonProps();

  return (
    <Box className={className}>
      <motion.div
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      >
        <Button
          {...buttonProps}
          sx={{
            minHeight: 44,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: theme.shadows[4],
            },
            '&:disabled': {
              opacity: 0.6,
            },
          }}
        />
      </motion.div>

      {/* Testing progress */}
      <AnimatePresence>
        {testState === 'testing' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {TEST_STEPS[currentStep]?.label || '処理中...'}
                  </Typography>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    {currentStep + 1}/{TEST_STEPS.length}
                  </Typography>
                </Box>
                
                <LinearProgress
                  variant="determinate"
                  value={(currentStep + 1) / TEST_STEPS.length * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                    },
                  }}
                />
              </Stack>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Test result */}
      <AnimatePresence>
        {testResult && testState !== 'testing' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <Alert
              id="test-result"
              severity={testResult.success ? 'success' : 'error'}
              sx={{ 
                mt: 2,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%',
                },
              }}
              action={
                testResult.details && (
                  <Button
                    size="small"
                    onClick={() => setShowDetails(!showDetails)}
                    sx={{ color: 'inherit' }}
                  >
                    {showDetails ? '隠す' : '詳細'}
                  </Button>
                )
              }
            >
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={600}>
                    {testResult.message}
                  </Typography>
                  {testResult.responseTime && (
                    <Chip
                      icon={<SpeedIcon sx={{ fontSize: '0.875rem' }} />}
                      label={`${testResult.responseTime}ms`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>

                <Collapse in={showDetails}>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    {testResult.details && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          詳細情報:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: alpha(theme.palette.background.paper, 0.5), p: 1, borderRadius: 1 }}>
                          {testResult.details}
                        </Typography>
                      </Box>
                    )}

                    {testResult.suggestions && testResult.suggestions.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          推奨対処法:
                        </Typography>
                        <Stack spacing={0.5}>
                          {testResult.suggestions.map((suggestion, index) => (
                            <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>•</Box>
                              {suggestion}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Typography variant="caption" color="text.secondary">
                      テスト実行時刻: {testResult.timestamp.toLocaleString('ja-JP')}
                    </Typography>
                  </Stack>
                </Collapse>
              </Stack>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Credentials reminder */}
      {!isCredentialsProvided && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          認証情報を入力してから接続テストを実行してください
        </Typography>
      )}
    </Box>
  );
});

ConnectionTestButton.displayName = 'ConnectionTestButton';

export default ConnectionTestButton;